import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Alert, Badge, Button, FormGroup, Input, Label, Spinner } from "reactstrap";

import { canManageClinicalRecords } from "../../helpers/clinicalRecordAccess";
import BeforeAfterSlider from "./BeforeAfterSlider";
import {
  ClientRecordAttachment,
  ClientRecordAttachmentType,
  deleteAttachment,
  downloadAttachmentBlob,
  getRecord,
  updateAttachmentMarketingOk,
  uploadAttachment,
} from "../../services/clientRecordService";

type ClientRecordAttachmentsPanelProps = {
  recordId: number;
  clientId?: number;
  canManage?: boolean;
  /** When true, staff may toggle marketing_ok (requires active image consent). */
  canUseInMarketing?: boolean;
  onAttachmentsChange?: () => void;
  onRequestImageConsent?: () => void;
};

const ATTACHMENT_TYPES: ClientRecordAttachmentType[] = ["before", "after", "other"];

const ClientRecordAttachmentsPanel = ({
  recordId,
  clientId,
  canManage: canManageProp,
  canUseInMarketing = false,
  onAttachmentsChange,
  onRequestImageConsent,
}: ClientRecordAttachmentsPanelProps) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canManage = canManageProp ?? canManageClinicalRecords();

  const [attachments, setAttachments] = useState<ClientRecordAttachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploadType, setUploadType] = useState<ClientRecordAttachmentType>("before");
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [marketingId, setMarketingId] = useState<number | null>(null);
  const [onlyMarketingOk, setOnlyMarketingOk] = useState(false);
  const [previewUrls, setPreviewUrls] = useState<Record<number, string>>({});

  const loadAttachments = useCallback(async (options?: { silent?: boolean }) => {
    if (!options?.silent) {
      setLoading(true);
    }

    try {
      const response = await getRecord(recordId);
      setAttachments(response.data.attachments ?? []);
    } catch (error: unknown) {
      if (!options?.silent) {
        const apiError = error as { data?: string };
        toast.error(apiError.data || t("clients.evolution.attachments.loadError"));
        setAttachments([]);
      }
    } finally {
      if (!options?.silent) {
        setLoading(false);
      }
    }
  }, [recordId, t]);

  useEffect(() => {
    void loadAttachments();
  }, [loadAttachments]);

  // Poll while any attachment is still processing (async Job).
  useEffect(() => {
    const hasPending = attachments.some((item) => item.status === "pending");

    if (!hasPending) {
      return;
    }

    const timer = window.setInterval(() => {
      void loadAttachments({ silent: true });
    }, 2500);

    return () => window.clearInterval(timer);
  }, [attachments, loadAttachments]);

  useEffect(() => {
    let cancelled = false;
    const createdUrls: string[] = [];

    const loadPreviews = async () => {
      const ready = attachments.filter((item) => item.status === "ready");

      await Promise.all(
        ready.map(async (attachment) => {
          try {
            const blob = await downloadAttachmentBlob(recordId, attachment.id);

            if (cancelled) {
              return;
            }

            const url = window.URL.createObjectURL(blob);
            createdUrls.push(url);
            setPreviewUrls((current) => ({ ...current, [attachment.id]: url }));
          } catch {
            // Preview optional — download button still works.
          }
        })
      );
    };

    void loadPreviews();

    return () => {
      cancelled = true;
      createdUrls.forEach((url) => window.URL.revokeObjectURL(url));
    };
  }, [attachments, recordId]);

  const visibleAttachments = useMemo(() => {
    if (!onlyMarketingOk) {
      return attachments;
    }

    return attachments.filter((item) => Boolean(item.marketing_ok));
  }, [attachments, onlyMarketingOk]);

  const grouped = useMemo(() => {
    const groups: Record<ClientRecordAttachmentType, ClientRecordAttachment[]> = {
      before: [],
      after: [],
      other: [],
    };

    visibleAttachments.forEach((item) => {
      if (groups[item.attachment_type]) {
        groups[item.attachment_type].push(item);
      } else {
        groups.other.push(item);
      }
    });

    return groups;
  }, [visibleAttachments]);

  const comparePair = useMemo(() => {
    const before = grouped.before.find((item) => item.status === "ready");
    const after = grouped.after.find((item) => item.status === "ready");

    if (!before || !after) {
      return null;
    }

    return { before, after };
  }, [grouped]);

  const renderConsentCta = () => {
    if (canUseInMarketing) {
      return null;
    }

    if (onRequestImageConsent) {
      return (
        <Button
          color="soft-primary"
          size="sm"
          type="button"
          onClick={onRequestImageConsent}
        >
          {t("clients.evolution.attachments.askConsentCta")}
        </Button>
      );
    }

    if (clientId) {
      return (
        <Button
          tag={Link}
          to={`/clients/${clientId}#client-image-marketing-consent`}
          color="soft-primary"
          size="sm"
        >
          {t("clients.evolution.attachments.askConsentCta")}
        </Button>
      );
    }

    return (
      <span className="small text-muted">
        {t("clients.evolution.attachments.askConsentHint")}
      </span>
    );
  };

  const resolveTypeLabel = (type: ClientRecordAttachmentType): string =>
    t(`clients.evolution.attachments.type.${type}`);

  const handlePickFile = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file || !canManage) {
      return;
    }

    setUploading(true);

    try {
      const response = await uploadAttachment(recordId, file, uploadType);
      toast.success(response.message || t("clients.evolution.attachments.uploadSuccess"));
      await loadAttachments();
      onAttachmentsChange?.();
    } catch (error: unknown) {
      const apiError = error as { data?: string };
      toast.error(apiError.data || t("clients.evolution.attachments.uploadError"));
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (attachment: ClientRecordAttachment) => {
    if (attachment.status !== "ready") {
      toast.info(t("clients.evolution.attachments.processingHint"));
      return;
    }

    try {
      const blob = await downloadAttachmentBlob(recordId, attachment.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = attachment.original_filename || `photo-${attachment.id}.jpg`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 30_000);
    } catch (error: unknown) {
      const apiError = error as { data?: string };
      toast.error(apiError.data || t("clients.evolution.attachments.downloadError"));
    }
  };

  const handleDelete = async (attachmentId: number) => {
    if (!canManage) {
      return;
    }

    setDeletingId(attachmentId);

    try {
      const response = await deleteAttachment(recordId, attachmentId);
      toast.success(response.message || t("clients.evolution.attachments.deleteSuccess"));
      await loadAttachments();
      onAttachmentsChange?.();
    } catch (error: unknown) {
      const apiError = error as { data?: string };
      toast.error(apiError.data || t("clients.evolution.attachments.deleteError"));
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggleMarketingOk = async (attachment: ClientRecordAttachment) => {
    if (!canManage) {
      return;
    }

    const nextValue = !attachment.marketing_ok;
    if (nextValue && !canUseInMarketing) {
      toast.error(t("clients.evolution.attachments.marketingNeedsConsent"));
      return;
    }

    setMarketingId(attachment.id);
    try {
      const response = await updateAttachmentMarketingOk(recordId, attachment.id, nextValue);
      toast.success(
        response.message || t("clients.evolution.attachments.marketingUpdated")
      );
      await loadAttachments();
      onAttachmentsChange?.();
    } catch (error: unknown) {
      const apiError = error as { data?: string };
      toast.error(apiError.data || t("clients.evolution.attachments.marketingError"));
    } finally {
      setMarketingId(null);
    }
  };

  if (loading) {
    return (
      <div className="d-flex align-items-center gap-2 text-muted small py-2">
        <Spinner size="sm" />
        {t("clients.evolution.attachments.loading")}
      </div>
    );
  }

  const hasAny = attachments.length > 0;
  const hasVisible = visibleAttachments.length > 0;

  return (
    <div className="mt-3 pt-3 border-top" data-clarity-mask="true">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
        <h6 className="mb-0">{t("clients.evolution.attachments.title")}</h6>
        {hasAny ? (
          <FormGroup check className="mb-0">
            <Input
              id={`attachments-marketing-filter-${recordId}`}
              type="checkbox"
              checked={onlyMarketingOk}
              onChange={(event) => setOnlyMarketingOk(event.target.checked)}
            />
            <Label
              check
              htmlFor={`attachments-marketing-filter-${recordId}`}
              className="small mb-0"
            >
              {t("clients.evolution.attachments.filterMarketingOk")}
            </Label>
          </FormGroup>
        ) : null}
      </div>

      {!canUseInMarketing ? (
        <Alert color="light" className="border py-2 mb-2">
          <p className="small mb-2 mb-md-0 d-md-inline me-md-2">
            {t("clients.evolution.attachments.marketingBlockedHint")}
          </p>
          {renderConsentCta()}
        </Alert>
      ) : null}

      {attachments.some((item) => item.status === "pending") ? (
        <p className="text-muted small mb-2">
          <Spinner size="sm" className="me-1" />
          {t("clients.evolution.attachments.pollingHint")}
        </p>
      ) : null}

      {comparePair &&
      previewUrls[comparePair.before.id] &&
      previewUrls[comparePair.after.id] ? (
        <div className="mb-3 p-2 border rounded bg-light">
          <p className="fw-medium small mb-2">
            {t("clients.evolution.attachments.compareTitle")}
          </p>
          <BeforeAfterSlider
            beforeSrc={previewUrls[comparePair.before.id]}
            afterSrc={previewUrls[comparePair.after.id]}
            maxHeight={200}
          />
          {!canUseInMarketing ? (
            <div className="mt-2 d-flex flex-wrap align-items-center gap-2">
              <span className="small text-muted">
                {t("clients.evolution.attachments.compareNeedsConsent")}
              </span>
              {renderConsentCta()}
            </div>
          ) : null}
        </div>
      ) : null}

      {!hasAny ? (
        <p className="text-muted small mb-3">{t("clients.evolution.attachments.empty")}</p>
      ) : !hasVisible ? (
        <p className="text-muted small mb-3">
          {t("clients.evolution.attachments.filterMarketingEmpty")}
        </p>
      ) : (
        ATTACHMENT_TYPES.map((type) => {
          const items = grouped[type];

          if (items.length === 0) {
            return null;
          }

          return (
            <div key={type} className="mb-3">
              <p className="fw-medium small mb-2">{resolveTypeLabel(type)}</p>
              <div className="d-flex flex-wrap gap-2">
                {items.map((attachment) => (
                  <div
                    key={attachment.id}
                    className="border rounded p-2 bg-white"
                    style={{ width: 120 }}
                  >
                    {attachment.status === "ready" && previewUrls[attachment.id] ? (
                      <img
                        src={previewUrls[attachment.id]}
                        alt={attachment.original_filename}
                        className="rounded mb-2 w-100"
                        style={{ height: 72, objectFit: "cover" }}
                      />
                    ) : (
                      <div
                        className="rounded bg-light d-flex align-items-center justify-content-center mb-2 text-muted small"
                        style={{ height: 72 }}
                      >
                        {attachment.status === "pending"
                          ? t("clients.evolution.attachments.processing")
                          : t("clients.evolution.attachments.failed")}
                      </div>
                    )}
                    <Badge
                      color={
                        attachment.marketing_ok && canUseInMarketing
                          ? "success"
                          : "light"
                      }
                      className="mb-1 w-100 text-wrap"
                    >
                      {attachment.marketing_ok && canUseInMarketing
                        ? t("clients.evolution.attachments.marketingOkBadge")
                        : t("clients.evolution.attachments.marketingNoBadge")}
                    </Badge>
                    <div className="d-flex flex-column gap-1">
                      <Button
                        color="soft-primary"
                        size="sm"
                        type="button"
                        disabled={attachment.status !== "ready"}
                        onClick={() => void handleDownload(attachment)}
                      >
                        {t("clients.evolution.attachments.download")}
                      </Button>
                      {canManage &&
                      (attachment.attachment_type === "before" ||
                        attachment.attachment_type === "after") ? (
                        <Button
                          color={attachment.marketing_ok ? "soft-secondary" : "soft-success"}
                          size="sm"
                          type="button"
                          disabled={
                            marketingId === attachment.id ||
                            (!attachment.marketing_ok && !canUseInMarketing)
                          }
                          onClick={() => void handleToggleMarketingOk(attachment)}
                        >
                          {marketingId === attachment.id ? (
                            <Spinner size="sm" />
                          ) : attachment.marketing_ok ? (
                            t("clients.evolution.attachments.marketingClear")
                          ) : (
                            t("clients.evolution.attachments.marketingMark")
                          )}
                        </Button>
                      ) : null}
                      {canManage ? (
                        <Button
                          color="soft-danger"
                          size="sm"
                          type="button"
                          disabled={deletingId === attachment.id}
                          onClick={() => void handleDelete(attachment.id)}
                        >
                          {deletingId === attachment.id ? (
                            <Spinner size="sm" />
                          ) : (
                            t("clients.evolution.attachments.remove")
                          )}
                        </Button>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })
      )}

      {canManage ? (
        <div className="mt-2">
          <Label htmlFor={`attachment-type-${recordId}`} className="form-label small">
            {t("clients.evolution.attachments.uploadLabel")}
          </Label>
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <Input
              id={`attachment-type-${recordId}`}
              type="select"
              bsSize="sm"
              style={{ maxWidth: 160 }}
              value={uploadType}
              onChange={(event) =>
                setUploadType(event.target.value as ClientRecordAttachmentType)
              }
            >
              {ATTACHMENT_TYPES.map((type) => (
                <option key={type} value={type}>
                  {resolveTypeLabel(type)}
                </option>
              ))}
            </Input>
            <Button
              color="primary"
              size="sm"
              type="button"
              disabled={uploading}
              onClick={handlePickFile}
            >
              {uploading ? (
                <Spinner size="sm" className="me-1" />
              ) : (
                <i className="ri-upload-2-line me-1" />
              )}
              {t("clients.evolution.attachments.uploadCta")}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="d-none"
              onChange={(event) => void handleFileChange(event)}
            />
          </div>
          <p className="text-muted small mb-0 mt-1">
            {t("clients.evolution.attachments.uploadHint")}
          </p>
        </div>
      ) : null}

      {hasAny ? (
        <Badge color="soft-info" className="mt-2">
          {t("clients.evolution.attachments.count", { count: attachments.length })}
        </Badge>
      ) : null}
    </div>
  );
};

export default ClientRecordAttachmentsPanel;
